<script setup lang="ts">
import currency from 'currency.js';
import numeral from 'numeral';

const { trackEvent } = useGtm();
const profileStore = useProfileStore();

const ticketPrice = ref();
const processingFeeRate = numeral(PROCESSING_FEE_RATE * 100).format(COMMA_WITH_SINGLE_DIGIT);
const processingFeeAdditional = numeral(PROCESSING_FEE_ADDITIONAL * 100).format(COMMA);

// Platform fee is calculated as a percentage of the ticket price,
// plus an additional fixed fee
// Ex. 100 * 0.05
const platformFee = computed(() => {
	if (!ticketPrice.value) {
		return 0;
	}
	return currency(ticketPrice.value).multiply(PLATFORM_FEE_RATE).format();
});

// Processing fee is calculated as a percentage of the ticket price
// Ex. (100 * 0.029) + 0.30
const processingFee = computed(() => {
	if (!ticketPrice.value) {
		return 0;
	}
	return currency(ticketPrice.value).multiply(PROCESSING_FEE_RATE).add(PROCESSING_FEE_ADDITIONAL).format();
});

// Buyer payment is equal to the original ticket price
// due to the organizer already absorbed fees for customers
const buyerPay = computed(() => {
	if (!ticketPrice.value) {
		return 0;
	}
	return currency(ticketPrice.value).format();
});

// Organizer's net income is the original ticket price
// minus platform fee and credit card fee
const youReceive = computed(() => {
	if (!ticketPrice.value) {
		return 0;
	}
	return currency(ticketPrice.value).subtract(platformFee.value).subtract(processingFee.value).format();
});

// Handles changes to the ticket price by converting it to a currency value
// with a precision of 2 decimal places.
function handlePriceChange() {
	if (!ticketPrice.value) {
		return;
	}
	ticketPrice.value = currency(ticketPrice.value, { precision: 2 });

	trackEvent({
		event: GTM_EVENT_NAME.CALCULATE_YOUR_TICKET_FEES_SUCCESS,
		user_id: profileStore.profileId,
	});
}
</script>

<template>
  <div class="calculator-page-container">
    <h1 class="text-display-sm text-semibold text-center">
      Calculate ticket fees
    </h1>

    <div class="calculator-container">
      <BaseTextInput
        v-model="ticketPrice"
        class="ticket-price"
        label="Ticket price"
        placeholder="1.00"
        @change="handlePriceChange"
      >
        <template #leftIcon>
          <span class="currency text-md text-medium">
            $
          </span>
        </template>
      </BaseTextInput>

      <div class="info-container">
        <div class="fees-container text-sm text-regular">
          <div class="value-tiem">
            <span>Peatix platform fee</span>
            <span>{{ platformFee }}</span>
          </div>
          <div class="value-tiem">
            <span>Credit card processing fee*</span>
            <span>{{ processingFee }}</span>
          </div>
        </div>

        <div class="line" />

        <div class="total-container text-sm text-medium">
          <div class="value-tiem">
            <span>Ticket price</span>
            <span class="text-semibold">{{ buyerPay }}</span>
          </div>
          <div class="value-tiem">
            <span>You receive</span>
            <span class="text-semibold">{{ youReceive }}</span>
          </div>
        </div>
      </div>

      <p class="terms text-sm text-left">
        <span class="asterisk">*</span>
        Stripe, our payment processor, typically applies a credit card fee of {{ processingFeeRate }}% plus {{ processingFeeAdditional }} cents per transaction.
      </p>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.calculator-page-container {
  max-width: rem(400);
  display: flex;
  flex-direction: column;
  row-gap: spacings-get(6);
  margin: 0 auto;
  padding-top: rem(120);

  @include media-query-max(mobile) {
    margin: spacings-get(8) spacings-get(4) 0;
    padding-top: 0;
  }

  .calculator-container {
    display: flex;
    flex-direction: column;
    row-gap: spacings-get(4);

    .currency {
      margin-left: spacings-get(4);
    }
  }

  .info-container {
    display: flex;
    flex-direction: column;
    padding: spacings-get(3);
    color: colors-get(gray, 600);
    background-color: colors-get(gray, 50);
    @include border-radius-lg;

    .fees-container,
    .total-container {
      display: flex;
      flex-direction: column;
      row-gap: spacings-get(3);
    }

    .value-tiem {
      display: flex;
      justify-content: space-between;
    }

    .line {
      height: rem(1);
      margin: spacings-get(3) 0;
      background-color: colors-get(gray, 200);
    }
  }

  .terms {
    color: colors-get(gray, 600);
  }
}
</style>
